export type RecentWorkspaceResourceType =
    | 'home'
    | 'inbox'
    | 'search'
    | 'folders'
    | 'drafts'
    | 'archive'
    | 'investments'
    | 'memos'
    | 'pipelines'
    | 'lifecycles'
    | 'dashboards'
    | 'lists'
    | 'tasks'
    | 'teams'
    | 'profile'
    | 'settings'
    | 'relations'
    | 'trash'
    | 'route';

export type RecentWorkspaceRoute = {
    path: string;
    resourceType: RecentWorkspaceResourceType;
    resourceId?: string;
    parentResourceId?: string;
    title: string;
    subtitle: string;
    website?: string;
    listType?: string;
    locked?: boolean;
    archived?: boolean;
    visitedAt: string;
};

const normalizePathname = (path: string) => {
    const pathname = path.split(/[?#]/, 1)[0] || '/';
    const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

    return normalized || '/';
};

const CANONICAL_ROUTE_ALIASES: Record<string, string> = {
    '/login': '/',
    '/register': '/',
    '/relations': '/relations/dashboards',
    '/settings': '/settings/account/general/overview',
    '/settings/account': '/settings/account/general/overview',
    '/settings/account/general': '/settings/account/general/overview',
    '/settings/account/notifications': '/settings/account/notifications/general',
    '/settings/account/plans': '/settings/account/plans/billing',
    '/settings/account/preferences': '/settings/account/preferences/overview',
    '/settings/workspace': '/settings/workspace/general/overview',
    '/settings/workspace/advanced': '/settings/workspace/advanced/appearance',
    '/settings/workspace/developers': '/settings/workspace/developers/api',
    '/settings/workspace/developers/oauth': '/settings/workspace/developers/applications',
    '/settings/workspace/general': '/settings/workspace/general/overview',
    '/settings/workspace/permissions': '/settings/workspace/permissions/members',
    '/settings/workspace/security': '/settings/workspace/security/overview',
};

const ROUTE_TERM_LABELS: Record<string, string> = {
    api: 'API',
    oauth: 'OAuth',
    saml: 'SAML',
    scim: 'SCIM',
    sso: 'SSO',
};

const SETTINGS_PAGE_LABELS: Record<string, Record<string, string>> = {
    account: {
        general: 'General',
        notifications: 'Notifications',
        plans: 'Plans',
        preferences: 'Preferences',
        security: 'Security',
        sessions: 'Sessions',
    },
    workspace: {
        about: 'About',
        advanced: 'Advanced',
        developers: 'Developers',
        general: 'General',
        permissions: 'Permissions',
        security: 'Security',
        workflows: 'Workflows',
    },
};

const SETTINGS_NESTED_ROUTE_LABELS: Record<string, string> = {
    'account/general/connections': 'Data Sources',
    'account/general/overview': 'Overview',
    'account/notifications/alerts': 'Alerts',
    'account/notifications/general': 'General',
    'account/plans/addons': 'Add-Ons',
    'account/plans/billing': 'Billing',
    'account/plans/invoices': 'Invoices',
    'account/plans/seats': 'Seats',
    'account/preferences/overview': 'Overview',
    'account/preferences/tags': 'Tags',
    'account/preferences/watchlists': 'Watchlists',
    'workspace/advanced/access': 'Access',
    'workspace/advanced/appearance': 'Appearance',
    'workspace/advanced/identity': 'Identity',
    'workspace/advanced/usage': 'Usage',
    'workspace/developers/api': 'API',
    'workspace/developers/applications': 'OAuth Applications',
    'workspace/developers/webhooks': 'Webhooks',
    'workspace/general/overview': 'Overview',
    'workspace/general/preferences': 'Preferences',
    'workspace/permissions/domains': 'Verified Domains',
    'workspace/permissions/members': 'Members',
    'workspace/permissions/roles': 'Roles',
    'workspace/permissions/teams': 'Teams',
    'workspace/security/events': 'Event Streaming',
    'workspace/security/logs': 'Audit Logs',
    'workspace/security/overview': 'Overview',
    'workspace/security/retention': 'Data Retention',
};

export const canonicalizeWorkspacePath = (path: string) => {
    const normalizedPath = normalizePathname(path);
    const legacyTeamRoute = normalizedPath.match(/^\/teams(?:\/(.+))?$/i);

    if (legacyTeamRoute) {
        return legacyTeamRoute[1]
            ? `/settings/workspace/permissions/teams/${legacyTeamRoute[1]}`
            : '/settings/workspace/permissions/teams';
    }

    return CANONICAL_ROUTE_ALIASES[normalizedPath.toLowerCase()] ?? normalizedPath;
};

export const titleizeRouteSegment = (value: string) =>
    value
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map(
            (segment) => ROUTE_TERM_LABELS[segment.toLowerCase()] ?? segment.charAt(0).toUpperCase() + segment.slice(1),
        )
        .join(' ');

export const getSettingsRouteTitle = (path: string) => {
    const segments = canonicalizeWorkspacePath(path).split('/').filter(Boolean);
    const scope = segments[1];
    const page = segments[2];
    const nestedRoute = segments[3];

    if (!page) {
        return scope ? titleizeRouteSegment(scope) : 'Settings';
    }

    const pageLabel = SETTINGS_PAGE_LABELS[scope]?.[page] ?? titleizeRouteSegment(page);

    if (!nestedRoute) {
        return pageLabel;
    }

    const nestedLabel =
        SETTINGS_NESTED_ROUTE_LABELS[`${scope}/${page}/${nestedRoute}`] ?? titleizeRouteSegment(nestedRoute);

    return `${pageLabel} › ${nestedLabel}`;
};

export const getRecentRouteKey = (entry: RecentWorkspaceRoute) =>
    entry.resourceId ? `${entry.resourceType}:${entry.resourceId}` : entry.path;

export const dedupeRecentRoutes = (entries: RecentWorkspaceRoute[]) => {
    const seen = new Set<string>();

    return entries.filter((entry) => {
        const key = getRecentRouteKey(entry);

        if (seen.has(key)) {
            return false;
        }

        seen.add(key);

        return true;
    });
};
